/** Faixa de métricas do upload, na página de detalhe.
 *
 *  Separa ESPERAR de PROCESSAR de propósito: "o upload levou 4
 *  minutos" não diz o que fazer, "esperou 3min40s na fila e processou
 *  em 20s" diz que o worker estava parado, não que a linha é lenta.
 *
 *  A conta linhas ↔ menções fecha pelos dois lados: menções ACIMA de
 *  posts distintos = post citando mais de uma marca (uma linha → 2+
 *  menções); linhas sem menção aparecem separadas por causa, porque as
 *  causas pedem reações diferentes — "sem termo" é "confira os termos
 *  das marcas", "já existiam" é reenvio e não há nada a fazer. */

import type { UploadSummary } from "@/shared/api/api-client";
import {
  formatDuration,
  formatMoment,
  formatMomentFull,
  formatThroughput,
  uploadTiming
} from "./upload-metrics";

type Props = { upload: UploadSummary; now: number };

export function UploadMetrics({ upload, now }: Props) {
  const timing = uploadTiming(upload, now);
  const multiBrandExtra = upload.mention_count - upload.distinct_post_count;

  const cells: Array<{ label: string; value: string; hint?: string }> = [
    { label: "Enviado", value: formatMoment(upload.uploaded_at) },
    {
      label: "Espera na fila",
      value: formatDuration(timing.queueSeconds),
      hint: timing.queueSeconds === null ? "ainda não começou" : undefined
    },
    {
      label: "Processamento",
      value: formatDuration(timing.runSeconds),
      hint: timing.running ? "em andamento" : undefined
    },
    { label: "Vazão", value: formatThroughput(timing.rowsPerSecond) },
    // O tempo acima é o do processamento original e não muda quando a fila
    // de correção é reprocessada; esta linha diz de quando é a última
    // linha reprocessada (só aparece se houve alguma).
    ...(upload.last_reprocessed_at
      ? [
          {
            label: "Reprocessado em",
            value: formatMomentFull(upload.last_reprocessed_at)
          }
        ]
      : []),
    {
      label: "Menções geradas",
      value: String(upload.mention_count),
      hint:
        multiBrandExtra > 0
          ? `${multiBrandExtra} extra${multiBrandExtra === 1 ? "" : "s"} de post citando mais de uma marca`
          : undefined
    },
    {
      label: "Sem termo de marca",
      value: String(upload.no_brand_match_count),
      hint:
        upload.no_brand_match_count > 0
          ? "na fila de correção: cole o texto da matéria ou descarte a linha"
          : undefined
    },
    {
      label: "Já existiam",
      value: String(upload.already_ingested_count),
      hint:
        upload.already_ingested_count > 0
          ? "posts repetidos de upload anterior"
          : undefined
    },
    {
      label: "Cadastro ignorado",
      value: String(upload.catalog_ignored_count),
      // Sem tom de alerta: diferente dos dois de cima, este número não
      // pede conferência nenhuma — é a empresa tendo dito que aquele
      // canal/veículo não entra, e o sistema obedecendo.
      hint:
        upload.catalog_ignored_count > 0
          ? "canal/veículo marcado pra não entrar"
          : undefined
    }
  ];

  return (
    <section>
      <h4 className="upload-detail-heading">Métricas</h4>
      <dl className="upload-metrics">
        {cells.map(cell => (
          <div key={cell.label} className="upload-metric">
            <dt>{cell.label}</dt>
            <dd>
              {cell.value}
              {cell.hint && <span className="upload-metric-hint">{cell.hint}</span>}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
